import { Suspense, useEffect, useRef, useState, type ComponentType } from "react";
import { useDismiss } from "../../design/useDismiss.ts";
import type { SessionView } from "@nabla/api";
import { code, Later, pointUnder, themes, useClient, useSession, useTheme, type Point } from "../../client/index.ts";
import { Icon } from "../../design/Icon.tsx";
import { nabla } from "../../design/icons/index.ts";
import { shellCopy } from "../copy.ts";
import { ErrorBoundary } from "../errors/ErrorBoundary.tsx";
import { Link } from "../navigation/Link.tsx";
import { useNavigation } from "../navigation/Navigation.tsx";
import { Freshness } from "./Freshness.tsx";
import { Search } from "./Search.tsx";
import styles from "./TopBar.module.css";

const menu = code(() => import("./Menu.tsx"));

interface TopBarProps {
  readonly onMenu: () => void;
  readonly onHelp: () => void;
  readonly onAsk: ((at: Point) => void) | undefined;
  readonly searchKey: number;
  readonly Answers?: ComponentType | undefined;
}

export function TopBar({ onMenu, onHelp, onAsk, searchKey, Answers }: TopBarProps) {
  const session = useSession();
  const client = useClient();
  const { route } = useNavigation();
  const [theme, setTheme] = useTheme();
  const scrolled = useScrolled();
  const brand = session.status === "dentro" ? session.view.ui.brand.name : "";
  const view = session.status === "dentro" ? session.view : undefined;
  const nextTheme = themes[(themes.indexOf(theme) + 1) % themes.length] ?? "sistema";
  return (
    <header className={scrolled ? `${styles.bar} ${styles.scrolled}` : styles.bar}>
      <button type="button" className={styles.icon} aria-label={shellCopy.menu} onClick={onMenu}>
        <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true" focusable="false">
          <path d="M3 5.5h14M3 10h14M3 14.5h14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>
      <Link href="/" className={styles.mark} aria-label={shellCopy.marca}>
        <Icon svg={nabla} size={24} />
      </Link>
      <Workspace name={brand} view={view} path={route.url} />
      <Search focusKey={searchKey} />
      {onAsk === undefined ? null : (
        <button type="button" className={styles.ask} onClick={(event) => onAsk(pointUnder(event.currentTarget))}>
          {shellCopy.perguntar.botao}
        </button>
      )}
      <div className={styles.end}>
        <Freshness />
        {Answers ? (
          <ErrorBoundary view={route.url} report={client.reportError} fallback={null}>
            <Suspense fallback={null}>
              <Answers />
            </Suspense>
          </ErrorBoundary>
        ) : null}
        <button type="button" className={styles.icon} aria-label={shellCopy.tema} data-tema={theme} onClick={() => setTheme(nextTheme)}>
          <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true" focusable="false">
            <circle cx="10" cy="10" r="6.25" fill="none" stroke="currentColor" strokeWidth="1.5" />
            <path d={theme === "claro" ? "M10 3.75a6.25 6.25 0 0 0 0 12.5z" : theme === "escuro" ? "M10 3.75a6.25 6.25 0 0 1 0 12.5z" : "M10 3.75v12.5"} fill="currentColor" stroke="currentColor" strokeWidth="1.5" />
          </svg>
        </button>
        <button type="button" className={`${styles.icon} ${styles.help}`} aria-label={shellCopy.atalhos.titulo} onClick={onHelp}>
          {shellCopy.teclaAjuda}
        </button>
      </div>
    </header>
  );
}

function Workspace({ name, view, path }: { readonly name: string; readonly view: SessionView | undefined; readonly path: string }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useDismiss(box, open, () => setOpen(false));
  if (view === undefined || (view.links.length === 0 && view.agency === undefined)) return <span className={styles.workspace}>{name}</span>;
  return (
    <div className={styles.workspaceBox} ref={box} data-menu-aberto={open ? "" : undefined}>
      <button type="button" className={styles.workspace} aria-expanded={open} onClick={() => setOpen(!open)}>
        {name} <span className={styles.caret}>{shellCopy.abrirLista}</span>
      </button>
      {open ? (
        <div className={styles.workspaceMenu}>
          <Later parts={menu}>{({ default: Menu }) => <Menu view={view} path={path} />}</Later>
        </div>
      ) : null}
    </div>
  );
}

function useScrolled(): boolean {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const check = () => setScrolled(scrollY > 0);
    addEventListener("scroll", check, { passive: true });
    return () => removeEventListener("scroll", check);
  }, []);
  return scrolled;
}
